<script setup lang="ts">
import type { VariantProps } from 'class-variance-authority';
import type { PrimitiveProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { cva } from 'class-variance-authority';
import { Primitive } from 'reka-ui';

type BadgeVariants = VariantProps<typeof badgeVariants>;

// `as` left undefined falls back to the Primitive's own default, a div
const properties = defineProps<
  PrimitiveProps & {
    variant?: BadgeVariants['variant'];
    radius?: BadgeVariants['radius'];
    appearance?: BadgeVariants['appearance'];
    size?: BadgeVariants['size'];
    state?: BadgeVariants['state'];
    class?: HTMLAttributes['class'];
  }
>();

// the `enabled:` prefixed classes only match a real <button>: a div never has that state, so the
// decorative badges keep their exact rendering, and the before: pseudo-element widens the touch
// target to 44px around a size-l badge without changing its visible height
const badgeVariants = cva(
  'relative inline-flex items-center gap-1 w-fit py-0.5 border leading-tight font-normal touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary-500 focus-visible:ring-offset-2 enabled:cursor-pointer enabled:before:absolute enabled:before:inset-x-0 enabled:before:-inset-y-1.5 enabled:before:content-empty',
  {
    variants: {
      variant: {
        green: 'bg-green-50 text-green-700 border-green-200',
        'light-blue': 'text-light-blue-700 bg-light-blue-50 border-light-blue-200 backdrop-blur',
        'dark-blue': 'bg-dark-blue-50 text-dark-blue-700 border-dark-blue-200',
        gray: 'bg-gray-50 text-text-secondary-600 border-gray-400',
        red: 'bg-red-50 text-red-700 border-red-200',
        orange: 'bg-orange-50 text-orange-800 border-orange-200',
        burgundy: 'bg-burgundy-50 text-burgundy-700 border-burgundy-200',
        teal: 'bg-teal-50 text-teal-700 border-teal-200',
        purple: 'bg-purple-50 text-purple-700 border-purple-200',
        brand: 'bg-brand-primary-50 text-brand-primary-700 border-brand-primary-200',
        translucent: 'bg-white/10 text-white border-white/20 backdrop-blur',
      },
      radius: {
        l: 'rounded-full',
        m: 'rounded-md',
      },
      appearance: {
        solid: '',
        outline: 'bg-transparent border-gray-300',
      },
      size: {
        l: 'h-8 gap-1.5 pl-2 pr-1.5 text-sm [&_svg]:size-3.5',
        m: 'h-6 px-1.5 text-sm',
        s: 'h-6 px-1.5 text-xs',
      },
      state: {
        disabled: 'opacity-60',
      },
    },
    defaultVariants: {
      variant: 'green',
      radius: 'l',
      appearance: 'solid',
      size: 'm',
    },
  },
);
</script>

<template>
  <Primitive
    :as="as"
    :as-child="asChild"
    :class="cn(badgeVariants({ variant, radius, appearance, size, state }), properties.class)"
  >
    <slot />
  </Primitive>
</template>
